import {ViewChild} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {ToasterService} from 'angular2-toaster';
import {TranslateService} from '@ngx-translate/core';
import {saveAs} from 'file-saver/FileSaver';
import {ChangesSubject, DataChangesObserver, LOCALE_CONFIG, TitleService} from '@common-lib';

import {ConfirmPopupComponent} from '../../components';
import {ConfigurationService} from '../../pages-services';

export abstract class ConfigurationBaseComponent {

    selectedLanguage = LOCALE_CONFIG.DEFAULT_LANGUAGE;
    selectedLocale = LOCALE_CONFIG.DEFAULT_LOCALE;
    abstract resource = '';

    protected state: any = {};
    protected dataSaving = false;

    protected changesSubject: ChangesSubject;
    protected changesCount = 0;
    protected haveChanges = false;
    protected checkedItems = [];

    @ViewChild('confirmPopupTemplate')
    confirmPopup: ConfirmPopupComponent;

    changeLanguageConfirmationMessage: string;

    constructor(protected title: TitleService,
                protected route: ActivatedRoute,
                protected toasterService: ToasterService,
                protected dataChanges: DataChangesObserver,
                protected translateService: TranslateService,
                protected configurationService: ConfigurationService) {
        this.title.setTitle(this.route.snapshot.data.title || '');
        this.translateService.get('webpanel.pages.change_language_confirmation').toPromise()
            .then(message => this.changeLanguageConfirmationMessage = message.replace(/^\w/, c => c.toUpperCase()));
    }

    protected reloadItems() {
        this.loadItems().then(() => {
            this.dataSaving = false;
        });
    }

    protected abstract loadItems(): Promise<Object>;

    protected createChangesObservation(settings) {
        this.changesCount = 0;
        this.haveChanges = false;
        const subject = this.dataChanges.observe(settings);
        this.changesSubject = subject;
        subject.onChanges.subscribe((event) => {
            this.changesCount = event.subject.changesCount;
            this.haveChanges = this.changesCount > 0;
        });
        return subject.getProxy();
    }

    toggleItem({target}, itemIndex) {
        target.checked
            ? this.checkedItems = [...this.checkedItems || [], itemIndex]
            : this.checkedItems.splice(this.checkedItems.indexOf(itemIndex), 1);
    }

    deleteItems() {
        const idsForDelete = [];
        this.state.items = this.state.items.filter((item, index) => {
            if (this.checkedItems.indexOf(index) === -1) {
                return true;
            } else {
                if (item.id) {
                    idsForDelete.push(item.id);
                }
                return false;
            }
        });
        if (idsForDelete.length) {
            const ids = idsForDelete.join(',');
            this.configurationService.delete(this.resource, ids).subscribe(res => {});
        }
        this.checkedItems = [];
    }

    addNewItem() {
        this.state.items.push({edit: true});
    }

    editItem(item) {
        item.edit = true;
    }

    cancelChanges() {
        this.reloadItems();
    }

    saveAll() {
        this.state.items.forEach(item => this.saveItem(item));
    }

    saveItem(item) {
        if (item.edit && item.id) {
            this.updateItem(item);
        }
        if (!item.id) {
            this.createItem(item);
        }
        this.dataSaving = true;
        this.changesCount = 0;
        this.haveChanges = false;
        delete item.edit;
    }

    private createItem(item: any) {
        this.configurationService.createItem(this.resource, item, this.selectedLocale).subscribe(
            async (res) => {
                if (res.success) {
                    this.reloadItems();
                    const successMessage = await this.translateService.get('webpanel.configuration.save.item.success_message').toPromise();
                    this.toasterService.pop('success', '', successMessage);
                }
            },
            async (error) => {
                this.reloadItems();
                const errorMessage = await this.translateService.get('webpanel.configuration.save.item.error_message').toPromise();
                this.toasterService.pop('error', errorMessage, error.message);
            },
        );
    }

    protected updateItem(item: any) {
        this.configurationService.updateItem(this.resource, item, this.selectedLocale).subscribe(
            async (res) => {
                if (res.success) {
                    this.reloadItems();
                    const successMessage = await this.translateService.get('webpanel.configuration.save.item.success_message').toPromise();
                    this.toasterService.pop('success', '', successMessage);
                }
            },
            async (error) => {
                this.reloadItems();
                const errorMessage = await this.translateService.get('webpanel.configuration.save.item.error_message').toPromise();
                this.toasterService.pop('error', errorMessage, error.message);
            }
        );
    }

    localeOpen() {
        if (this.changesCount > 0) {
            this.confirmPopup.show();
        }
    }

    localeChanged(language) {
        this.selectedLanguage = language;
        this.selectedLocale = language.locales.length ? language.locales[0] : LOCALE_CONFIG.DEFAULT_LOCALE;
        this.reloadItems();
    }

    confirmPopupAction(action) {
        this.confirmPopup.hide();
        if (action) {
            this.reloadItems();
        }
    }
}



// WEBPACK FOOTER //
// ./src/app/pages/configuration-base/configuration-base.component.ts